import React, { useState, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import ReAnimated from 'react-native-reanimated';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ADMIN_COLORS } from '@/constants/admin';
import { useAccordionAnimation } from '@/hooks/common/useAccordionAnimation';

interface AccordionProps {
  header: React.ReactNode;
  children: React.ReactNode;
  forceOpen?: number;
  defaultOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  openWhen?: boolean;
  closeWhen?: boolean;
  headerRightComponent?: React.ReactNode;
}

export const Accordion: React.FC<AccordionProps> = ({
  header,
  children,
  forceOpen,
  defaultOpen = false,
  onToggle,
  openWhen,
  closeWhen,
  headerRightComponent,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const {
    open: animOpen,
    close: animClose,
    setOpen: animSetOpen,
    animatedHeightStyle,
    animatedChevronStyle,
    onContentLayout,
  } = useAccordionAnimation({ initiallyOpen: defaultOpen });

  useEffect(() => {
    if (forceOpen !== undefined && forceOpen > 0) {
      setIsOpen(true);
      animOpen();
    }
  }, [forceOpen, animOpen]);

  useEffect(() => {
    if (closeWhen) {
      setIsOpen(false);
      animClose();
      return;
    }
    if (openWhen) {
      setIsOpen(true);
      animOpen();
    }
  }, [openWhen, closeWhen, animOpen, animClose]);

  const toggleAccordion = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    animSetOpen(newIsOpen);
    onToggle?.(newIsOpen);
  };

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <TouchableOpacity
          style={styles.headerTouchable}
          onPress={toggleAccordion}
          activeOpacity={0.7}
        >
          <View style={styles.headerContent}>
            {header}
          </View>
        </TouchableOpacity>
        {headerRightComponent && (
          <View style={styles.headerRightContainer}>
            {headerRightComponent}
          </View>
        )}
        <TouchableOpacity
          style={styles.chevronTouchable}
          onPress={toggleAccordion}
          activeOpacity={0.7}
        >
          <ReAnimated.View style={animatedChevronStyle}>
            <SVG_COMPONENTS.CHEVRON_DOWN
              width={20}
              height={20}
              color={ADMIN_COLORS.TEXT_SECONDARY}
            />
          </ReAnimated.View>
        </TouchableOpacity>
      </View>
      
      <ReAnimated.View style={animatedHeightStyle}>
        <View onLayout={onContentLayout} style={styles.contentInner}>
          <View style={styles.contentContainer}>
            {children}
          </View>
        </View>
      </ReAnimated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderRadius: 12,
    marginBottom: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  headerTouchable: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 20,
    paddingVertical: 20,
  },
  headerContent: {
    flex: 1,
  },
  chevronTouchable: {
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  headerRightContainer: {
  },
  contentInner: {
    position: 'absolute',
    width: '100%',
  },
  contentContainer: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
  },
});
